import * as pdfjsLib from 'pdfjs-dist';
import { cleanExtractedText } from './textCleaner';
import { extractTextWithOCR, isPDFImageBased, type OCRResult } from './ocrExtractor';

// Aggressively disable PDF.js worker to prevent 404 errors
// Set to empty string to completely disable worker loading
pdfjsLib.GlobalWorkerOptions.workerSrc = '';

// Configure PDF.js to run without worker - aggressive settings
const pdfJsConfig = {
  disableWorker: true,
  useWorkerFetch: false,
  isEvalSupported: false,
  useSystemFonts: true,
  standardFontDataUrl: '',
  maxImageSize: -1,
  cMapUrl: '',
  cMapPacked: false,
  // Force disable all worker-related functionality
  enableXfa: false,
  disableAutoFetch: true,
  disableStream: true
};

// Enhanced logging for debugging
function debugLog(message: string, data?: any) {
  console.log(`🔍 PDF-Extract: ${message}`, data ? JSON.stringify(data, null, 2) : '');
}

// Text sanitization utility - removes PDF artifacts but preserves readable content
function sanitizeText(text: string): string {
  if (!text) return '';
  
  // Basic sanitization only - comprehensive cleaning will be done later
  return text
    .replace(/\u0000/g, '') // Remove null bytes
    .replace(/[\u0001-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '') // Remove control characters
    .trim();
}


export interface PDFExtractionResult {
  success: boolean;
  text: string;
  pageCount: number;
  error?: string;
  extractionMethod: 'pdfjs_text_content' | 'pdfjs_text_layer' | 'fallback_text_extraction' | 'ocr';
  ocrConfidence?: number;
  isImageBased?: boolean;
}

/**
 * Fallback text extraction that looks for actual readable content
 */
const fallbackTextExtraction = async (file: File): Promise<PDFExtractionResult> => {
  try {
    debugLog('🔄 Using intelligent fallback text extraction method');
    
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const pdfString = decoder.decode(uint8Array);
    
    let extractedText = '';
    
    // Method 1: Look for text in parentheses (common PDF text encoding)
    const parenthesesMatches = pdfString.match(/\(([^)]{3,})\)/g) || [];
    for (const match of parenthesesMatches) {
      const text = match.slice(1, -1); // Remove parentheses
      // Only include text that looks like readable content
      if (/^[a-zA-Z0-9\s.,!?@-]+$/.test(text) && text.length > 2) {
        extractedText += text + ' ';
      }
    }
    
    // Method 2: Look for text after 'Tj' operators (PDF text showing operators)
    const tjMatches = pdfString.match(/\(([^)]+)\)\s*Tj/g) || [];
    for (const match of tjMatches) {
      const text = match.match(/\(([^)]+)\)/)?.[1];
      if (text && /^[a-zA-Z0-9\s.,!?@-]+$/.test(text) && text.length > 2) {
        extractedText += text + ' ';
      }
    }
    
    // Method 3: Look for readable text patterns in the PDF
    const readableMatches = pdfString.match(/[A-Z][a-zA-Z\s]{10,}/g) || [];
    for (const match of readableMatches) {
      if (!/^[A-Z]{3,}$/.test(match) && // Skip all-caps abbreviations
          !match.includes('PDF') && 
          !match.includes('obj') &&
          !match.includes('endobj')) {
        extractedText += match + ' ';
      }
    }
    
    // Apply basic sanitization first
    const sanitizedText = sanitizeText(extractedText);

    debugLog('📊 Fallback: After sanitization', {
      length: sanitizedText.length,
      preview: sanitizedText.substring(0, 300) + '...'
    });

    // Then apply comprehensive cleaning
    const cleaningResult = cleanExtractedText(sanitizedText);
    const cleanText = cleaningResult.cleanedText;

    debugLog('📊 Fallback: After comprehensive cleaning', {
      originalLength: cleaningResult.originalLength,
      cleanedLength: cleaningResult.cleanedLength,
      compressionRatio: cleaningResult.metadata.compressionRatio,
      preview: cleanText.substring(0, 300) + '...'
    });

    // Minimum 50 characters threshold (aligned with server-side validation)
    if (cleanText.length >= 50) {
      debugLog('✅ Fallback extraction found readable text', {
        finalLength: cleanText.length,
        preview: cleanText.substring(0, 200) + '...'
      });

      return {
        success: true,
        text: cleanText,
        pageCount: 1,
        extractionMethod: 'fallback_text_extraction'
      };
    }

    debugLog('⚠️ Fallback extraction found insufficient text', {
      cleanTextLength: cleanText.length,
      sanitizedTextLength: sanitizedText.length,
      rawTextLength: extractedText.length
    });

    throw new Error(`Insufficient readable text found: ${cleanText.length} characters (minimum 50 required). PDF may be image-based or have unsupported encoding.`);
  } catch (error: any) {
    debugLog('❌ Fallback extraction failed', { error: error.message });
    return {
      success: false,
      text: '',
      pageCount: 0,
      error: error.message,
      extractionMethod: 'fallback_text_extraction'
    };
  }
};

/**
 * Extract text from PDF file using PDF.js (client-side) without worker
 */
export const extractTextFromPDF = async (file: File): Promise<PDFExtractionResult> => {
  try {
    debugLog('🚀 Starting client-side PDF text extraction with PDF.js');
    debugLog('📄 File details', {
      name: file.name,
      size: file.size,
      type: file.type,
      sizeKB: Math.round(file.size / 1024)
    });

    // Validate file
    if (!file) {
      throw new Error('No file provided');
    }

    if (file.type !== 'application/pdf') {
      throw new Error('File must be a PDF');
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      throw new Error('PDF file too large (max 10MB)');
    }

    if (file.size === 0) {
      throw new Error('PDF file is empty');
    }

    // Convert file to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    debugLog('📦 File converted to ArrayBuffer', { bytes: arrayBuffer.byteLength });

    // Load PDF document WITHOUT worker
    debugLog('📖 Loading PDF document with PDF.js...');
    
    try {
      const pdf = await pdfjsLib.getDocument({
        data: arrayBuffer,
        verbosity: 0,
        ...pdfJsConfig,
        useSystemFonts: true
      }).promise;

      debugLog('✅ PDF loaded successfully', {
        numPages: pdf.numPages,
        fingerprint: pdf.fingerprints?.[0] || 'unknown'
      });

      let fullText = '';
      const pageTexts: string[] = [];

      // Extract text from each page (limit to first 20 pages for performance)
      const maxPages = Math.min(pdf.numPages, 20);
      
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        debugLog(`📄 Processing page ${pageNum}/${maxPages}...`);
        
        try {
          const page = await pdf.getPage(pageNum);
          
          // Get text content using PDF.js text extraction
          const textContent = await page.getTextContent({
            normalizeWhitespace: true,
            disableCombineTextItems: false
          });
          
          // Process text items to build readable text
          let pageText = '';
          let lastY = 0;

          // Check if textContent.items is iterable
          if (!textContent.items || !Array.isArray(textContent.items)) {
            debugLog(`⚠️ Page ${pageNum} has no text items or items is not an array`);
            continue;
          }

          debugLog(`📊 Page ${pageNum} has ${textContent.items.length} text items`);

          for (const item of textContent.items) {
            // Handle both TextItem and TextMarkedContent types
            const textStr = (item as any).str;

            if (textStr && textStr.trim()) {
              const transform = (item as any).transform;
              const currentY = transform ? transform[5] : 0;

              // Add line break if we've moved to a new line (significant Y change)
              if (pageText && Math.abs(currentY - lastY) > 5) {
                pageText += '\n';
              }

              pageText += textStr + ' ';
              lastY = currentY;
            }
          }
          
          if (pageText.trim()) {
            const cleanPageText = sanitizeText(pageText);
            if (cleanPageText.length > 10) {
              pageTexts.push(cleanPageText);
              debugLog(`✅ Page ${pageNum} processed`, { 
                textLength: cleanPageText.length,
                preview: cleanPageText.substring(0, 100) + '...'
              });
            }
          }
          
          // Clean up page resources
          page.cleanup();
          
        } catch (pageError) {
          debugLog(`⚠️ Error processing page ${pageNum}`, { error: pageError.message });
          // Continue with other pages
        }
      }

      // Combine all page texts
      fullText = pageTexts.join('\n\n').trim();

      debugLog('🎉 PDF.js text extraction completed!', {
        totalPages: pdf.numPages,
        pagesProcessed: maxPages,
        pagesWithText: pageTexts.length,
        totalCharacters: fullText.length,
        preview: fullText.substring(0, 300) + '...'
      });

      // Clean up PDF resources
      pdf.destroy();

      // Validate extracted text (minimum 50 characters)
      if (fullText.length < 50) {
        debugLog('⚠️ PDF.js extracted minimal text, checking if image-based', {
          extractedLength: fullText.length,
          preview: fullText
        });

        // Check if PDF is image-based
        const isImageBased = await isPDFImageBased(file);

        if (isImageBased) {
          debugLog('🖼️ PDF is image-based, attempting OCR extraction');
          return await tryOCRExtraction(file, pdf.numPages);
        }

        debugLog('📄 PDF is not image-based, trying fallback text extraction');
        return await fallbackTextExtraction(file);
      }

      // Final text cleaning
      debugLog('🧹 Starting comprehensive text cleaning', {
        rawTextLength: fullText.length,
        preview: fullText.substring(0, 300) + '...'
      });

      const cleaningResult = cleanExtractedText(fullText);
      const finalText = cleaningResult.cleanedText;

      debugLog('🧹 Applied comprehensive text cleaning', {
        originalLength: cleaningResult.originalLength,
        cleanedLength: cleaningResult.cleanedLength,
        compressionRatio: cleaningResult.metadata.compressionRatio,
        hadMetadata: cleaningResult.metadata.hadAdobeMetadata || cleaningResult.metadata.hadMicrosoftMetadata,
        preview: finalText.substring(0, 300) + '...'
      });

      // Validate cleaned text has meaningful content
      if (finalText.length < 50) {
        debugLog('⚠️ Cleaned text too short after cleaning, checking if image-based', {
          cleanedLength: finalText.length,
          originalLength: fullText.length
        });

        // Check if PDF is image-based before trying OCR
        const isImageBased = await isPDFImageBased(file);

        if (isImageBased) {
          debugLog('🖼️ PDF is image-based, attempting OCR extraction');
          return await tryOCRExtraction(file, pdf.numPages);
        }

        debugLog('📄 Trying fallback text extraction');
        return await fallbackTextExtraction(file);
      }
      
      return {
        success: true,
        text: finalText,
        pageCount: pdf.numPages,
        extractionMethod: 'pdfjs_text_content'
      };

    } catch (pdfJsError: any) {
      debugLog('❌ PDF.js failed, trying fallback', { error: pdfJsError.message });
      
      // If PDF.js fails, try fallback method
      return await fallbackTextExtraction(file);
    }

  } catch (error: any) {
    debugLog('❌ PDF text extraction failed completely', { error: error.message });
    
    // Provide helpful error messages
    let errorMessage = error.message;
    
    if (error.message?.includes('Invalid PDF')) {
      errorMessage = 'The uploaded file is not a valid PDF document. Please ensure you upload a proper PDF file.';
    } else if (error.message?.includes('Password')) {
      errorMessage = 'This PDF is password-protected. Please upload an unprotected PDF file.';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'PDF processing timed out. The file may be too complex or corrupted.';
    } else if (error.message?.includes('worker')) {
      errorMessage = 'PDF processing failed due to browser limitations. Please try a different PDF file.';
    } else if (error.message?.includes('Loading')) {
      errorMessage = 'Failed to load PDF. The file may be corrupted or in an unsupported format.';
    }

    return {
      success: false,
      text: '',
      pageCount: 0,
      error: errorMessage,
      extractionMethod: 'pdfjs_text_content'
    };
  }
};

/**
 * Validate if a file is a proper PDF
 */
export const validatePDFFile = (file: File): { valid: boolean; error?: string } => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (file.type !== 'application/pdf') {
    return { valid: false, error: 'File must be a PDF. Please select a .pdf file.' };
  }

  if (file.size === 0) {
    return { valid: false, error: 'File is empty' };
  }

  if (file.size > 10 * 1024 * 1024) {
    return { valid: false, error: `File too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum size is 10MB.` };
  }

  // Check file extension as additional validation
  const fileName = file.name.toLowerCase();
  if (!fileName.endsWith('.pdf')) {
    return { valid: false, error: 'File must have a .pdf extension.' };
  }

  // Basic file content validation
  if (file.size < 1024) { // Less than 1KB is suspicious for a PDF
    return { valid: false, error: 'File appears to be too small to be a valid PDF document.' };
  }

  return { valid: true };
};

/**
 * Quick pre-upload check to see if PDF likely contains extractable text
 * This performs a fast check before upload to provide early feedback
 */
export const quickPDFTextCheck = async (file: File): Promise<{
  hasText: boolean;
  confidence: 'high' | 'medium' | 'low';
  message: string;
}> => {
  try {
    debugLog('🔍 Performing quick PDF text check');

    // Read first 100KB of file for quick check
    const chunkSize = Math.min(file.size, 100 * 1024);
    const chunk = file.slice(0, chunkSize);
    const arrayBuffer = await chunk.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const pdfString = decoder.decode(uint8Array);

    // Check for text content indicators
    const hasTextObjects = /\(([^)]{10,})\)/.test(pdfString);
    const hasTjOperators = /Tj|TJ/.test(pdfString);
    const hasReadableText = /[A-Za-z]{5,}/.test(pdfString);

    // Check for image-only indicators
    const imageCount = (pdfString.match(/\/Image|\/DCTDecode|\/JPXDecode/g) || []).length;
    const textCount = (pdfString.match(/\/Text|Tj|TJ/g) || []).length;

    debugLog('📊 Quick PDF check results', {
      hasTextObjects,
      hasTjOperators,
      hasReadableText,
      imageCount,
      textCount,
      chunkSize
    });

    // Determine confidence level
    if (hasTjOperators && hasReadableText && textCount > imageCount) {
      return {
        hasText: true,
        confidence: 'high',
        message: 'PDF appears to contain extractable text'
      };
    } else if (hasTextObjects || hasReadableText) {
      return {
        hasText: true,
        confidence: 'medium',
        message: 'PDF may contain extractable text'
      };
    } else if (imageCount > 3 && textCount === 0) {
      return {
        hasText: false,
        confidence: 'high',
        message: 'PDF appears to be image-based (scanned). Text extraction may fail. Consider using OCR or re-exporting as a text-based PDF.'
      };
    } else {
      return {
        hasText: false,
        confidence: 'low',
        message: 'Unable to detect text in PDF. Extraction may fail.'
      };
    }
  } catch (error: any) {
    debugLog('⚠️ Quick PDF check failed', { error: error.message });
    return {
      hasText: true, // Default to optimistic
      confidence: 'low',
      message: 'Unable to pre-validate PDF content'
    };
  }
};

/**
 * Preview extracted text (first 500 characters)
 */
export const getTextPreview = (text: string, maxLength: number = 500): string => {
  if (!text) return '';
  
  if (text.length <= maxLength) {
    return text;
  }
  
  return text.substring(0, maxLength) + '...';
};

/**
 * Try OCR extraction as a fallback for image-based PDFs
 */
async function tryOCRExtraction(file: File, pageCount: number): Promise<PDFExtractionResult> {
  debugLog("🔤 Starting OCR extraction", { fileName: file.name, pages: pageCount });

  try {
    const ocrResult: OCRResult = await extractTextWithOCR(file, {
      language: "eng",
      onProgress: (progress) => {
        debugLog(`OCR Progress: ${progress}%`);
      }
    });

    if (!ocrResult.success) {
      return {
        success: false,
        text: "",
        pageCount,
        error: ocrResult.error || "OCR extraction failed",
        extractionMethod: "ocr",
        ocrConfidence: 0,
        isImageBased: true
      };
    }

    // Clean the OCR text
    const cleaningResult = cleanExtractedText(ocrResult.text);
    const finalText = cleaningResult.cleanedText;

    debugLog("✅ OCR extraction successful", {
      confidence: ocrResult.confidence,
      textLength: finalText.length,
      processingTime: ocrResult.processingTime
    });

    return {
      success: finalText.length >= 50,
      text: finalText,
      pageCount,
      extractionMethod: "ocr",
      ocrConfidence: ocrResult.confidence,
      isImageBased: true
    };
  } catch (error: any) {
    debugLog("❌ OCR extraction failed", { error: error.message });

    return {
      success: false,
      text: "",
      pageCount,
      error: `OCR extraction failed: ${error.message}`,
      extractionMethod: "ocr",
      ocrConfidence: 0,
      isImageBased: true
    };
  }
}
